if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        // Luxe often wraps the label in .qa-label – fall back to first span
        this.submitButtonText = this.submitButton.querySelector('.qa-label') || this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Keep price prefix inside the submit button when variant JS updates the label
        this.onVariantChangeUnsubscriber =
          typeof subscribe === 'function'
            ? subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
                const variant = event?.data?.variant;
                if (!variant) return;
                // Update the button price prefix for the currently selected variant
                this.updatePricePrefix(variant);
              })
            : null;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      formatMoney(cents) {
        const amount = Number(cents);

        // Use Shopify's formatter if present (this should respect your store money format settings)
        try {
          if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
            // If your store is set to no decimals, money_format should already be amount_no_decimals
            return window.Shopify.formatMoney(amount, window.Shopify.money_format);
          }
        } catch (e) {
          // fall through to Intl formatting
        }

        // Robust fallback: force NO decimals
        const currency =
          (window.Shopify && window.Shopify.currency && window.Shopify.currency.active)
            ? window.Shopify.currency.active
            : 'GBP';
        const value = amount / 100;

        try {
          return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(value);
        } catch (e) {
          // last-resort fallback
          return `£${Math.round(value)}`;
        }
      }

      updatePricePrefix(variant) {
        if (!this.submitButtonText) return;

        // Ensure the price span exists (theme JS may have overwritten the label)
        let priceSpan = this.submitButtonText.querySelector('.prod-submit-price');
        if (!priceSpan) {
          priceSpan = document.createElement('span');
          priceSpan.className = 'prod-submit-price';
          // Insert at start of label
          this.submitButtonText.insertBefore(priceSpan, this.submitButtonText.firstChild);
        }
        priceSpan.textContent = `${this.formatMoney(variant.price)} - `;
      }

      setSubmitLabel(text) {
        if (!this.submitButtonText) return;

        const priceSpan = this.submitButtonText.querySelector('.prod-submit-price');
        if (priceSpan) {
          // Preserve price span, replace everything after it with the label text
          const kept = priceSpan;
          while (this.submitButtonText.firstChild) {
            this.submitButtonText.removeChild(this.submitButtonText.firstChild);
          }
          this.submitButtonText.appendChild(kept);
          this.submitButtonText.appendChild(document.createTextNode(text));
        } else {
          this.submitButtonText.textContent = text;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.setSubmitLabel(text);
        } else {
          this.submitButton.removeAttribute('disabled');

          if (this.submitButton.classList.contains('button--preorder')) {
            this.setSubmitLabel(window.variantStrings.preOrder);
          } else {
            this.setSubmitLabel(window.variantStrings.addToCart);
          }
        }
      }

      disconnectedCallback() {
        if (typeof this.onVariantChangeUnsubscriber === 'function') this.onVariantChangeUnsubscriber();
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}